import { useState } from "react";
import type { ReactNode } from "react";
import { useAutoCloseOnSave } from "@/lib/use-auto-close-on-save";
import { EditPanelButtons } from "./EditPanelButtons";

// "Click to inline-edit" pattern for existing entries. Display = static
// JSX with hover affordance; click opens the edit form; the panel hosts
// ABBRECHEN + FERTIG buttons (see EditPanelButtons). Neither button
// saves - saving is owned by the page-level SaveBar (@adr SPARK-0005-unified-deferred-save-bar@1). FERTIG
// just closes the panel; ABBRECHEN closes and asks the caller to drop
// its overlay for this row.
//
// Auto-collapses after a successful global SaveBar save (via the shared
// useAutoCloseOnSave hook).

interface EditableRowProps {
  /**
   * Anker-Präfix der Zeile; der Bearbeitungskasten leitet seine Knöpfe davon ab.
   * Vom Aufrufer, weil mehrere Zeilen gleichzeitig offen sein können.
   */
  id?: string;
  editable?: boolean;
  renderDisplay: () => ReactNode;
  renderEdit: (args: { close: () => void }) => ReactNode;
  /**
   * Slot rendered in the top-right corner of the bordered edit box.
   * Used for per-entry meta controls (e.g. the share toggle) so they
   * don't compete with form fields or footer actions for attention.
   */
  topRight?: ReactNode;
  /**
   * Called when the user clicks ABBRECHEN. Should drop the row's local
   * overlay so the next display render shows the last committed value.
   * The row also closes automatically after this fires.
   */
  onCancel?: () => void;
  /**
   * Called when the user clicks LÖSCHEN. Should stage the row for
   * deletion via the SaveBar (same deferred model as edits/adds - no
   * immediate genesis call). When omitted (drafts), the button is not
   * rendered. The row closes automatically after this fires.
   */
  onDelete?: () => void;
  /**
   * Labels of required fields still empty inside the edit form. When
   * non-empty, FERTIG is disabled and clicking it surfaces a tooltip with
   * the missing labels.
   */
  missingFields?: readonly string[];
  /** Defaults to "Fertig". Use "Hinzufügen" for draft (new-entry) rows. */
  confirmLabel?: string;
  /** Zeilenbehälter in beiden Zuständen; `id` oben ist das Präfix der Bearbeiten-Knöpfe. */
  rowId?: string;
  /**
   * When true, opens the panel on mount. Used for freshly-added drafts
   * where it'd be silly to immediately ask the user to click again.
   */
  initiallyOpen?: boolean;
  className?: string;
}

export function EditableRow({
  id,
  rowId,
  editable = true,
  renderDisplay,
  renderEdit,
  topRight,
  onCancel,
  onDelete,
  missingFields = [],
  confirmLabel = "Fertig",
  initiallyOpen = false,
  className = "",
}: EditableRowProps) {
  const [editing, setEditing] = useState(initiallyOpen);
  useAutoCloseOnSave(editing, () => setEditing(false));

  if (editable && editing) {
    const close = () => setEditing(false);
    const cancel = () => {
      onCancel?.();
      close();
    };
    const remove = onDelete
      ? () => {
          onDelete();
          close();
        }
      : undefined;
    return (
      <div id={rowId} className={className}>
        <div className="space-y-3 rounded-md bg-page p-3 ring-1 ring-ink/10">
          {topRight ? <div className="flex justify-end">{topRight}</div> : null}
          {renderEdit({ close })}
          <EditPanelButtons
            id={id}
            onCancel={cancel}
            onConfirm={close}
            onDelete={remove}
            confirmLabel={confirmLabel}
            missingFields={missingFields}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      id={rowId}
      className={`${editable ? "group cursor-pointer" : ""} ${className}`}
      onClick={editable ? () => setEditing(true) : undefined}
      title={editable ? "Bearbeiten" : undefined}
    >
      {renderDisplay()}
    </div>
  );
}
